import firebase from 'firebase/functions';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import { z } from 'zod';
import { paths } from './apis/api-config/api-paths-v2.ts.types';
import { cloudFunctions } from './firebase-api';
import { parseAPISafeJSON } from './parseAPIJSON';

export function getAppVersion() {
  return process.env.NODE_ENV === 'test'
    ? 'test'
    : process.env.NODE_ENV === 'development'
    ? 'development'
    : process.env.REACT_APP_CONCUSSIONRX_APPVERSION;
}

const _concussionrx_appVersion = getAppVersion();

if (!_concussionrx_appVersion) {
  throw new Error(`'_concussionrx_appVersion' is not set`);
}

/**
 * Helper method to invoke Firebase Functions (e.g: OnCall and OnRequest) and pass required parameters to the API
 *
 * @deprecated Use `typedHttpsCallableWrapper` where possible
 * @param functionName
 * @returns
 */
export function httpsCallableWrapper<
  InType extends Record<string, any> | void,
  OutType
>(
  functionName: string,
  postProcess?: (a: { data: OutType }) => { data: OutType }
): (
  data?: InType
) => Promise<Omit<firebase.HttpsCallableResult, 'data'> & { data: OutType }> {
  return (data?: InType) =>
    // Strip any leading forward-slashes
    cloudFunctions
      .httpsCallable(functionName.replace(/^\//, ''))({
        ...data,

        /**
         * The appVersion is a required parameter for all CRx APIs
         * This helps the team see which app deployment version is invoking an API
         */
        _concussionrx_appVersion
      })
      .then(
        (_): Omit<firebase.HttpsCallableResult, 'data'> & { data: OutType } => {
          return postProcess ? postProcess(_) : parseAPISafeJSON(_);
        }
      );
}

/**
 * Typed method for working with callable functions and automatically inferring the input and return type
 *
 * @param fnName
 * @returns
 */
export function typedHttpsCallableWrapper<
  FnName extends keyof paths,
  /**
   * Infer the input type by constraining the type to configurations that use the POST method and accept JSON input
   */
  InType extends void | Record<string, any> = paths[FnName]['post'] extends {
    requestBody?: { content: { 'application/json': infer R } };
  }
    ? R extends { _concussionrx_appVersion: string } & Record<string, any>
      ? Omit<R, '_concussionrx_appVersion'>
      : void
    : void,
  /**
   * Infer the return type by constraining the type to the success ojbject
   */
  OutType = paths[FnName]['post'] extends {
    responses: { 200: { content: { 'application/json': infer R } } };
  }
    ? R
    : never
>(fnName: FnName, postProcess?: (a: { data: OutType }) => { data: OutType }) {
  return httpsCallableWrapper<InType, OutType>(fnName, postProcess);
}

export function typedOnRequestWrapper<
  FnName extends keyof paths,
  /**
   * Infer the input type by constraining the type to configurations that use the POST method and accept JSON input
   */
  InType extends void | Record<string, any> = paths[FnName]['get'] extends {
    parameters: { query: infer R };
  }
    ? R extends { _concussionrx_appVersion: string } & Record<string, any>
      ? Omit<R, '_concussionrx_appVersion'>
      : void
    : void,
  /**
   * Infer the return type by constraining the type to the success ojbject
   */
  OutType = paths[FnName]['get'] extends {
    responses: { 200: { content: { 'application/json': infer R } } };
  }
    ? R
    : never
>(fnName: FnName) {
  return onRequestWrapper<InType, OutType>(fnName);
}

/**
 *
 * @deprecated Use `typedOnRequestWrapper` where possible
 * @param path
 * @returns
 */
export function onRequestWrapper<In extends Record<string, any> | void, Out>(
  path: string
) {
  return (data: In) => {
    const url = new URL(process.env.REACT_APP_CLOUD_SERVER ?? '');

    url.pathname = `${url.pathname}/${path.replace(/^\//, '')}`;
    url.searchParams.set(
      '_concussionrx_appVersion',
      _concussionrx_appVersion ?? ''
    );

    /**
     * If data was provided, encode it in the URL
     */
    if (data !== undefined) {
      Object.entries(data).forEach(([key, val]) => {
        url.searchParams.set(key, String(val));
      });
    }
    return fetch(url.toString()).then(async _ => {
      if (_.ok) {
        return _.json() as Out;
      }

      throw OnRequestHTTPError.fromJSON(await _.json());
    });
  };
}

export class OnRequestHTTPError extends Error {
  public static JSON_SCHEMA = z.object({
    message: z.string(),
    status: z.enum([
      'NOT_FOUND',
      'INTERNAL',
      'INVALID_ARGUMENT',
      'FAILED_PRECONDITION',
      'PERMISSION_DENIED'
    ])
  });
  public static fromJSON(json: any): OnRequestHTTPError {
    try {
      const parsedJSON = OnRequestHTTPError.JSON_SCHEMA.parse(json);
      return new OnRequestHTTPError(parsedJSON.status, parsedJSON.message);
    } catch (e) {
      const error = resolveError(e);
      const error2 = new Error(
        `Failed to parse OnRequest error. ${
          error.message
        }. Error from server: ${JSON.stringify(json)}`,
        {
          cause: error.cause
        }
      );
      error2.stack = error.stack;

      throw error2;
    }
  }
  public toJSON() {
    return OnRequestHTTPError.JSON_SCHEMA.parse({
      message: this.message,
      status: this.status
    });
  }
  constructor(
    public readonly status: string,
    message: string,
    options?: ErrorOptions
  ) {
    super(message, options);
  }
}
